import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import type { CreatePayrollRunInput } from '@accounting/shared';
import { useCreatePayrollRun } from '../../hooks/usePayroll';
import { useActiveCompanyId } from '../../stores/useCompanyStore';
import { PayrollRunForm } from '../../components/payroll/PayrollRunForm';
import { ApiError } from '../../lib/api';

export const Route = createFileRoute('/_auth/payroll/new')({
  component: NewPayrollRunPage,
});

function NewPayrollRunPage() {
  const companyId = useActiveCompanyId();
  const navigate = useNavigate();
  const create = useCreatePayrollRun(companyId);

  if (!companyId) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: 32 }}>
        <p className="muted">No active company.</p>
        <Link to="/companies" className="btn-primary" style={{ display: 'inline-block', textDecoration: 'none' }}>Go to Companies</Link>
      </div>
    );
  }

  const errorMessage =
    create.error instanceof ApiError
      ? (create.error.body as { error?: string } | null)?.error ?? 'Could not post payroll run'
      : create.error?.message;

  // A 409 on this endpoint means one thing: a live run already exists for this
  // pay date. Offer the override rather than leaving a genuine second payroll
  // (a bonus run alongside regular pay) with nowhere to go.
  const duplicatePayDate =
    create.error instanceof ApiError && create.error.status === 409;

  return (
    <div>
      <Link to="/payroll" style={{ fontSize: 13 }}>← Payroll</Link>
      <h1 style={{ fontSize: 24, margin: '8px 0 16px' }}>New payroll run</h1>

      <PayrollRunForm
        companyId={companyId}
        submitLabel="Post payroll run"
        submitting={create.isPending}
        errorMessage={errorMessage}
        duplicatePayDate={duplicatePayDate}
        cancelTo="/payroll"
        onSubmit={(input: CreatePayrollRunInput) =>
          create.mutate(input, { onSuccess: (r) => void navigate({ to: '/payroll/$id', params: { id: r.id } }) })
        }
      />
    </div>
  );
}
